(ns metabase.lib.query.test-spec
  (:refer-clojure :exclude [mapv name])
  (:require
   [clojure.string :as str]
   [malli.core :as mc]
   [malli.transform :as mtx]
   [medley.core :as m]
   [metabase.lib.aggregation :as lib.aggregation]
   [metabase.lib.binning :as lib.binning]
   [metabase.lib.breakout :as lib.breakout]
   [metabase.lib.expression :as lib.expression]
   [metabase.lib.fe-util :as lib.fe-util]
   [metabase.lib.field :as lib.field]
   [metabase.lib.filter :as lib.filter]
   [metabase.lib.join :as lib.join]
   [metabase.lib.join.util :as lib.join.util]
   [metabase.lib.limit :as lib.limit]
   [metabase.lib.metadata :as lib.metadata]
   [metabase.lib.metadata.calculation :as lib.metadata.calculation]
   [metabase.lib.native :as lib.native]
   [metabase.lib.options :as lib.options]
   [metabase.lib.order-by :as lib.order-by]
   [metabase.lib.query :as lib.query]
   [metabase.lib.ref :as lib.ref]
   [metabase.lib.schema :as lib.schema]
   [metabase.lib.schema.binning :as lib.schema.binning]
   [metabase.lib.schema.expression :as lib.schema.expression]
   [metabase.lib.schema.join :as lib.schema.join]
   [metabase.lib.schema.metadata :as lib.schema.metadata]
   [metabase.lib.schema.ref :as lib.schema.ref]
   [metabase.lib.schema.template-tag :as lib.schema.template-tag]
   [metabase.lib.schema.temporal-bucketing :as lib.schema.temporal-bucketing]
   [metabase.lib.schema.test-spec :as lib.schema.test-spec]
   [metabase.lib.stage :as lib.stage]
   [metabase.lib.temporal-bucket :as lib.temporal-bucket]
   [metabase.util :as u]
   [metabase.util.malli :as mu]
   [metabase.util.malli.registry :as mr]
   [metabase.util.performance :refer [mapv]]))

(mu/defn- find-source :- [:or ::lib.schema.metadata/table ::lib.schema.metadata/card]
  [metadata-providerable         :- ::lib.schema.metadata/metadata-providerable
   {spec-id :id spec-type :type} :- ::lib.schema.test-spec/test-source-spec]
  (case spec-type
    :table (lib.metadata/table metadata-providerable spec-id)
    :card  (lib.metadata/card metadata-providerable spec-id)))

(defn- deduplicated-name [column]
  ((some-fn :lib/deduplicated-name :name) column))

(defn- original-name [column]
  ((some-fn :lib/original-name :name) column))

(mu/defn- matches-join-and-fk? :- :boolean
  [{:keys [join-alias source-field-id source-field-join-alias]} :- ::lib.schema.test-spec/test-order-by-spec
   column                                                        :- ::lib.schema.metadata/column]
  (and (= join-alias (lib.join.util/current-join-alias column))
       (= source-field-id (:fk-field-id column))
       (= source-field-join-alias (:fk-join-alias column))))

(mu/defn- matches-column? :- :boolean
  [{:keys [name field-id] :as column-spec} :- ::lib.schema.test-spec/test-order-by-spec
   column                                  :- ::lib.schema.metadata/column]
  (and (if field-id
         (= field-id (:id column))
         (= name (deduplicated-name column)))
       (matches-join-and-fk? column-spec column)))

(mu/defn- check-unambiguous-name :- :nil
  "Throws when `column` shares its original name with another of `available-columns` that only a deduplication
  suffix tells apart from it."
  [available-columns :- [:sequential ::lib.schema.metadata/column]
   column-spec       :- ::lib.schema.test-spec/test-order-by-spec
   column            :- ::lib.schema.metadata/column]
  (when-let [others (seq (filter #(and (= (original-name column) (original-name %))
                                       (not= (deduplicated-name column) (deduplicated-name %))
                                       (matches-join-and-fk? column-spec %))
                                 available-columns))]
    (throw (ex-info (str "Ambiguous column: " (count others) " other column(s) are also named "
                         (pr-str (original-name column)))
                    {:column-spec column-spec, :columns (cons column others)}))))

(mu/defn- find-column :- ::lib.schema.metadata/column
  "Finds the one column a spec names, by field ID or else by deduplicated name. A name is refused when its column
  shares its original name with another column that only a deduplication suffix tells apart, like a previous stage's
  `ID` and `ID_2`; a field broken out twice is named apart instead."
  [_query            :- ::lib.schema/query
   _stage-number     :- :int
   available-columns :- [:sequential ::lib.schema.metadata/column]
   column-spec       :- ::lib.schema.test-spec/test-order-by-spec]
  (let [columns (filterv (partial matches-column? column-spec) available-columns)]
    (case (count columns)
      0 (throw (ex-info "No column found" {:columns available-columns, :column-spec column-spec}))
      1 (let [column (first columns)]
          (when-not (:field-id column-spec)
            (check-unambiguous-name available-columns column-spec column))
          column)
      (throw (ex-info "Multiple columns found" {:columns columns, :column-spec column-spec})))))

(mu/defn- append-fields :- ::lib.schema/query
  [query        :- ::lib.schema/query
   stage-number :- :int
   field-specs  :- [:sequential ::lib.schema.test-spec/test-column-spec]]
  (let [visible (lib.metadata.calculation/visible-columns query stage-number)]
    (->> field-specs
         (mapv (partial find-column query stage-number visible))
         (lib.field/with-fields query stage-number))))

(mu/defn- matches-temporal-bucket? :- :boolean
  [unit   :- ::lib.schema.temporal-bucketing/unit
   option :- ::lib.schema.temporal-bucketing/option]
  (= unit (:unit option)))

(mu/defn- find-temporal-bucket :- ::lib.schema.temporal-bucketing/option
  [query        :- ::lib.schema/query
   stage-number :- :int
   column       :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]
   unit         :- ::lib.schema.temporal-bucketing/unit]
  (let [temporal-buckets (lib.temporal-bucket/available-temporal-buckets query stage-number column)
        matches (filterv (partial matches-temporal-bucket? unit) temporal-buckets)]
    (case (count matches)
      0 (throw (ex-info "No temporal bucket found" {:temporal-buckets temporal-buckets, :unit unit}))
      1 (first matches)
      (throw (ex-info "Multiple temporal buckets found" {:matches matches, :unit unit})))))

(mu/defn- add-temporal-bucket :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]
  [query        :- ::lib.schema/query
   stage-number :- :int
   unit         :- ::lib.schema.temporal-bucketing/unit
   column       :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]]
  (->> (find-temporal-bucket query stage-number column unit)
       (lib.temporal-bucket/with-temporal-bucket column)))

(mu/defn- matches-binning? :- :boolean
  [{:keys [strategy] :as binning} :- ::lib.schema.test-spec/test-binning-spec
   {:keys [mbql]}                 :- ::lib.schema.binning/binning-option]
  (and (= strategy (:strategy mbql))
       (or (= strategy :default)
           (== (strategy binning) (strategy mbql)))))

(defn- binning-error-message [{:keys [strategy num-bins]} binning-strategies]
  (let [supported-counts (keep (comp :num-bins :mbql) binning-strategies)]
    (if (and (= strategy :num-bins) (seq supported-counts))
      (str "Unsupported bin count " num-bins ". Supported counts for this field are "
           (str/join ", " (butlast supported-counts)) ", and " (last supported-counts) ".")
      "No binning strategy found")))

(mu/defn- find-binning-strategy :- ::lib.schema.binning/binning-option
  [query        :- ::lib.schema/query
   stage-number :- :int
   binning      :- ::lib.schema.test-spec/test-binning-spec
   column       :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]]
  (let [binning-strategies (lib.binning/available-binning-strategies query stage-number column)
        matches (filterv (partial matches-binning? binning) binning-strategies)]
    (case (count matches)
      0 (throw (ex-info (binning-error-message binning binning-strategies)
                        {:binning-strategies binning-strategies :binning binning}))
      1 (first matches)
      (throw (ex-info "Multiple binning strategies found" {:matches matches :binning binning})))))

(mu/defn- add-binning :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]
  [query        :- ::lib.schema/query
   stage-number :- :int
   binning      :- ::lib.schema.test-spec/test-binning-spec
   column       :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]]
  (->> (find-binning-strategy query stage-number binning column)
       (lib.binning/with-binning column)))

(mu/defn- apply-binning :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]
  [query                  :- ::lib.schema/query
   stage-number           :- :int
   {:keys [unit binning]} :- [:or ::lib.schema.test-spec/test-order-by-spec ::lib.schema.test-spec/test-join-source-spec]
   column                 :- [:or ::lib.schema.metadata/column ::lib.schema.ref/ref]]
  (cond->> column
    unit    (add-temporal-bucket query stage-number unit)
    binning (add-binning query stage-number binning)))

(mu/defn- append-breakout :- ::lib.schema/query
  [query               :- ::lib.schema/query
   stage-number        :- :int
   columns             :- [:sequential ::lib.schema.metadata/column]
   breakout-spec       :- ::lib.schema.test-spec/test-breakout-spec]
  (let [named?      (= :breakout (keyword (:type breakout-spec)))
        column-spec (if named? (:column breakout-spec) breakout-spec)
        column      (->> (find-column query stage-number columns column-spec)
                         (apply-binning query stage-number column-spec))]
    (lib.breakout/breakout query stage-number
                           (cond-> column
                             named? (-> lib.ref/ref
                                        (lib.options/update-options assoc :name (:name breakout-spec)))))))

(mu/defn- append-breakouts :- ::lib.schema/query
  [query          :- ::lib.schema/query
   stage-number   :- :int
   breakout-specs :- [:sequential ::lib.schema.test-spec/test-breakout-spec]]
  (let [columns (lib.breakout/breakoutable-columns query stage-number)]
    (reduce #(append-breakout %1 stage-number columns %2)
            query
            breakout-specs)))

(mu/defn- expression-spec->expression-parts
  [query             :- ::lib.schema/query
   stage-number      :- :int
   expression-spec   :- [:or
                         ::lib.schema.test-spec/test-expression-spec
                         ::lib.schema.test-spec/test-join-source-spec
                         ::lib.schema.test-spec/test-operator-arg-spec]
   available-columns :- [:sequential ::lib.schema.metadata/column]]
  (case (:type expression-spec)
    :literal  (:value expression-spec)
    :column   (find-column query stage-number available-columns expression-spec)
    :segment  (lib.metadata/segment query (:id expression-spec))
    :measure  (lib.metadata/measure query (:id expression-spec))
    :metric   (lib.metadata/metric query (:id expression-spec))
    :operator {:lib/type :mbql/expression-parts
               :operator (:operator expression-spec)
               :options {}
               :args (mapv #(expression-spec->expression-parts query stage-number % available-columns)
                           (:args expression-spec))}))

(mu/defn- named-expression-spec? :- :boolean
  [expression-spec :- [:or
                       ::lib.schema.test-spec/test-expression-spec
                       ::lib.schema.test-spec/test-join-source-spec
                       ::lib.schema.test-spec/test-named-expression-spec]]
  (and
   (contains? expression-spec :name)
   (contains? expression-spec :value)
   (not (contains? expression-spec :type))))

(mu/defn- expression-spec->expression-clause :- ::lib.schema.expression/expression
  [query                                    :- ::lib.schema/query
   stage-number                             :- :int
   {:keys [name value] :as expression-spec} :- [:or
                                                ::lib.schema.test-spec/test-expression-spec
                                                ::lib.schema.test-spec/test-join-source-spec
                                                ::lib.schema.test-spec/test-named-expression-spec]
   available-columns                        :- [:sequential ::lib.schema.metadata/column]]
  (if (named-expression-spec? expression-spec)
    (-> (expression-spec->expression-clause query stage-number value available-columns)
        (lib.expression/with-expression-name name))
    (-> (expression-spec->expression-parts query stage-number expression-spec available-columns)
        lib.fe-util/expression-clause)))

(mu/defn- append-expression :- ::lib.schema/query
  [query                :- ::lib.schema/query
   stage-number         :- :int
   {:keys [name value]} :- ::lib.schema.test-spec/test-named-expression-spec]
  ;; NOTE: expressionable-columns needs to calculated inside the loop
  ;; able to reference each other.
  (->> (lib.expression/expressionable-columns query stage-number nil)
       (expression-spec->expression-clause query stage-number value)
       (lib.expression/expression query stage-number name)))

(mu/defn- append-expressions :- ::lib.schema/query
  [query            :- ::lib.schema/query
   stage-number     :- :int
   expression-specs :- [:sequential ::lib.schema.test-spec/test-named-expression-spec]]
  (reduce #(append-expression %1 stage-number %2)
          query
          expression-specs))

(mu/defn- matches-strategy? :- :boolean
  [strategy-name      :- ::lib.schema.join/strategy
   {:keys [strategy]} :- ::lib.schema.join/strategy.option]
  (= strategy-name strategy))

(mu/defn- find-join-strategy :- ::lib.schema.join/strategy.option
  [query        :- ::lib.schema/query
   stage-number :- :int
   strategy     :- ::lib.schema.join/strategy]
  (let [available-strategies (lib.join/available-join-strategies query stage-number)]
    (if-let [found-strategy (m/find-first (partial matches-strategy? strategy) available-strategies)]
      found-strategy
      (throw (ex-info "No join strategy found" {:available-strategies available-strategies, :strategy strategy})))))

(mu/defn- join-condition-spec->join-condition :- ::lib.schema.join/condition
  [query        :- ::lib.schema/query
   stage-number :- :int
   target       :- [:or ::lib.schema.metadata/table ::lib.schema.metadata/card]
   {:keys [operator left right]} :- ::lib.schema.test-spec/test-join-condition-spec]
  (let [lhs (->> (lib.join/join-condition-lhs-columns query stage-number nil nil nil)
                 (expression-spec->expression-clause query stage-number left)
                 (apply-binning query stage-number left))
        rhs (->> (lib.join/join-condition-rhs-columns query stage-number target lhs nil)
                 (expression-spec->expression-clause query stage-number right)
                 (apply-binning query stage-number right))]
    (lib.fe-util/join-condition-clause operator lhs rhs)))

(mu/defn- append-join :- ::lib.schema/query
  [query        :- ::lib.schema/query
   stage-number :- :int
   {:keys [source strategy conditions], join-alias :alias} :- ::lib.schema.test-spec/test-join-spec]
  (let [join-target (find-source query source)
        join-strategy (find-join-strategy query stage-number strategy)
        join-conditions (if conditions
                          (mapv (partial join-condition-spec->join-condition query stage-number join-target) conditions)
                          (lib.join/suggested-join-conditions query stage-number join-target))
        join-clause (cond-> (lib.join/join-clause join-target join-conditions join-strategy)
                      join-alias (lib.join/with-join-alias join-alias))]
    (lib.join/join query stage-number join-clause)))

(mu/defn- append-joins :- ::lib.schema/query
  [query        :- ::lib.schema/query
   stage-number :- :int
   join-specs   :- [:sequential ::lib.schema.test-spec/test-join-spec]]
  (reduce #(append-join %1 stage-number %2)
          query
          join-specs))

(mu/defn- append-filter :- ::lib.schema/query
  [query        :- ::lib.schema/query
   stage-number :- :int
   filter-spec  :- [:or ::lib.schema.test-spec/test-expression-spec ::lib.schema.test-spec/test-segment-spec]]
  (if (= (keyword (:type filter-spec)) :segment)
    (if-let [segment (lib.metadata/segment query (:id filter-spec))]
      (lib.filter/filter query stage-number segment)
      (throw (ex-info "No segment found" {:segment-spec filter-spec})))
    (->> (lib.filter/filterable-columns query stage-number)
         (expression-spec->expression-clause query stage-number filter-spec)
         (lib.filter/filter query stage-number))))

(mu/defn- append-filters :- ::lib.schema/query
  [query        :- ::lib.schema/query
   stage-number :- :int
   filter-specs :- [:sequential [:or ::lib.schema.test-spec/test-expression-spec
                                 ::lib.schema.test-spec/test-segment-spec]]]
  (reduce #(append-filter %1 stage-number %2)
          query
          filter-specs))

(mu/defn- saved-aggregation-spec? :- :boolean
  [aggregation-spec :- ::lib.schema.test-spec/test-aggregation-spec]
  (boolean (#{:measure :metric} (keyword (:type aggregation-spec)))))

(mu/defn- saved-aggregation :- [:maybe [:or ::lib.schema.metadata/measure ::lib.schema.metadata/metric]]
  [query            :- ::lib.schema/query
   aggregation-spec :- ::lib.schema.test-spec/test-aggregation-spec]
  (case (keyword (:type aggregation-spec))
    :measure (lib.metadata/measure query (:id aggregation-spec))
    :metric  (lib.metadata/metric query (:id aggregation-spec))))

(mu/defn- append-aggregation :- ::lib.schema/query
  [query            :- ::lib.schema/query
   stage-number     :- :int
   aggregation-spec :- ::lib.schema.test-spec/test-aggregation-spec]
  (let [saved-spec (cond
                     (saved-aggregation-spec? aggregation-spec)                aggregation-spec
                     (some-> (:value aggregation-spec) saved-aggregation-spec?) (:value aggregation-spec))
        clause     (if saved-spec
                     (if-let [aggregation (saved-aggregation query saved-spec)]
                       (lib.ref/ref aggregation)
                       (throw (ex-info "No saved aggregation found" {:aggregation-spec saved-spec})))
                     (->> (lib.aggregation/aggregable-columns query stage-number nil)
                          (expression-spec->expression-clause query stage-number aggregation-spec)))]
    (lib.aggregation/aggregate query stage-number
                               (cond-> clause
                                 ;; Only the name a later stage refers to; the display name stays derived.
                                 (:name aggregation-spec) (lib.options/update-options assoc :name (:name aggregation-spec))))))

(mu/defn- append-aggregations  :- ::lib.schema/query
  [query             :- ::lib.schema/query
   stage-number      :- :int
   aggregation-specs :- [:sequential ::lib.schema.test-spec/test-aggregation-spec]]
  (reduce #(append-aggregation %1 stage-number %2)
          query
          aggregation-specs))

(mu/defn- append-order-by :- ::lib.schema/query
  [query               :- ::lib.schema/query
   stage-number        :- :int
   orderable-columns   :- [:sequential ::lib.schema.metadata/column]
   {:keys [direction]
    :as order-by-spec} :- ::lib.schema.test-spec/test-order-by-spec]
  (as-> (find-column query stage-number orderable-columns order-by-spec) column
    (apply-binning query stage-number order-by-spec column)
    (lib.order-by/order-by query column direction)))

(mu/defn- append-order-bys :- ::lib.schema/query
  [query          :- ::lib.schema/query
   stage-number   :- :int
   order-by-specs :- [:sequential ::lib.schema.test-spec/test-order-by-spec]]
  (let [orderable-columns (lib.order-by/orderable-columns query stage-number)]
    (reduce #(append-order-by %1 stage-number orderable-columns %2)
            query
            order-by-specs)))

(mu/defn- append-stage-clauses :- ::lib.schema/query
  [query        :- ::lib.schema/query
   stage-number :- :int
   {:keys [fields expressions filters joins aggregations breakouts order-bys limit]} :- ::lib.schema.test-spec/test-stage-spec]
  (cond-> query
    (> stage-number 0) (lib.stage/append-stage)
    joins              (append-joins stage-number joins)
    expressions        (append-expressions stage-number expressions)
    breakouts          (append-breakouts stage-number breakouts)
    aggregations       (append-aggregations stage-number aggregations)
    order-bys          (append-order-bys stage-number order-bys)
    filters            (append-filters stage-number filters)
    fields             (append-fields stage-number fields)
    limit              (lib.limit/limit stage-number limit)))

(mr/def ::raw-spec
  "A test query spec as callers write it, possibly with string or camelCase keys and without defaults, before parsing."
  [:map {:closed false, ::mr/deliberately-open true, :description "an unparsed test query spec"}])

(def parse-query-spec
  "Parser for query-spec."
  (mc/decoder [:ref ::lib.schema.test-spec/test-query-spec]
              (mtx/transformer
               mtx/json-transformer
               (mtx/key-transformer {:decode #(-> % u/->kebab-case-en keyword)})
               {:name :normalize}
               mtx/strip-extra-keys-transformer
               mtx/default-value-transformer)))

(mu/defn test-query :- ::lib.schema/query
  "Creates a query from a test query spec."
  [metadata-providerable :- ::lib.schema.metadata/metadata-providerable
   query-spec            :- ::raw-spec]
  (let [{:keys [stages]} (parse-query-spec query-spec)
        source           (->> stages first :source (find-source metadata-providerable))
        query            (lib.query/query metadata-providerable source)]
    (reduce-kv append-stage-clauses query stages)))

(mu/defn- field-id->field-ref :- :mbql.clause/field
  [query    :- ::lib.schema/query
   field-id :- pos-int?]
  (->> field-id
       (lib.metadata/field query)
       lib.ref/ref))

(mu/defn- adjust-template-tag
  [query :- ::lib.schema/query
   spec  :- ::lib.schema.test-spec/test-template-tag-spec]
  (u/update-if-exists spec :dimension #(field-id->field-ref query %)))

(mu/defn- adjust-template-tags :- ::lib.schema.template-tag/template-tags
  [query                  :- ::lib.schema/query
   inferred-template-tags :- ::lib.schema.template-tag/template-tags
   template-tags-spec     :- [:maybe ::lib.schema.test-spec/test-template-tags-spec]]
  (let [spec-tags              (for [spec template-tags-spec]
                                 (adjust-template-tag query spec))
        inferred-tag-name->tag (into {} (map (juxt :name identity)) inferred-template-tags)
        spec-tag-name->tag     (into {} (map (juxt :name identity)) spec-tags)
        tag-names              (into []
                                     (comp cat
                                           (map :name)
                                           (distinct))
                                     [inferred-template-tags
                                      spec-tags])]
    (into []
          (map (fn [tag-name]
                 (merge (inferred-tag-name->tag tag-name)
                        (spec-tag-name->tag tag-name))))
          tag-names)))

(mu/defn- add-template-tags :- ::lib.schema/query
  [query              :- ::lib.schema/query
   template-tags-spec :- [:maybe ::lib.schema.test-spec/test-template-tags-spec]]
  (let [inferred-template-tags (or (lib.native/template-tags query) [])]
    (->> template-tags-spec
         (adjust-template-tags query inferred-template-tags)
         (lib.native/with-template-tags query))))

(def ^:private parse-native-query-spec
  "Parser for native-query-spec."
  (mc/coercer [:ref ::lib.schema.test-spec/test-native-query-spec]
              (mtx/transformer
               mtx/json-transformer
               (mtx/key-transformer {:decode #(-> % u/->kebab-case-en keyword)})
               {:name :normalize}
               mtx/strip-extra-keys-transformer
               mtx/default-value-transformer)))

(mu/defn test-native-query :- ::lib.schema/query
  "Creates a native query from a test native query spec."
  [metadata-providerable :- ::lib.schema.metadata/metadata-providerable
   native-query-spec     :- ::raw-spec]
  (let [{:keys [query template-tags]} (parse-native-query-spec native-query-spec)]
    (-> (lib.native/native-query metadata-providerable query)
        (add-template-tags template-tags))))
